import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, Camera, Repeat, CheckCircle } from 'lucide-react';

const BatchUpload = ({ onNotification, themeClasses, isDarkMode }) => {
    const [frontFile, setFrontFile] = useState(null);
    const [backFile, setBackFile] = useState(null);
    const [frontPreview, setFrontPreview] = useState(null);
    const [backPreview, setBackPreview] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [lastUploadStatus, setLastUploadStatus] = useState(null); // 'success', 'error'
    const frontInputRef = useRef(null);
    const backInputRef = useRef(null);

    const API_URL = '/api';

    // Cleanup object URLs
    useEffect(() => {
        if (!frontFile) { setFrontPreview(null); return; }
        const url = URL.createObjectURL(frontFile);
        setFrontPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [frontFile]);

    useEffect(() => {
        if (!backFile) { setBackPreview(null); return; }
        const url = URL.createObjectURL(backFile);
        setBackPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [backFile]);

    const handleFileChange = (e, side) => {
        const file = e.target.files[0];
        if (file) {
            if (side === 'front') setFrontFile(file);
            else setBackFile(file);
        }
    };

    const handleDrop = (e, side) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            if (side === 'front') setFrontFile(file);
            else setBackFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Helper: Resize and compress image client-side to speed up upload
    const resizeImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 2500; // High enough for numismatic details
                    const MAX_HEIGHT = 2500;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    canvas.toBlob((blob) => {
                        // Return new file with jpeg compression
                        resolve(new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        }));
                    }, 'image/jpeg', 0.85);
                };
            };
        });
    };

    const handleSubmit = async () => {
        if (!frontFile) {
            onNotification("Front image is required", "error");
            return;
        }

        setIsUploading(true);
        setLastUploadStatus(null);

        try {
            // Resize images before upload
            const processedFront = await resizeImage(frontFile);
            const processedBack = backFile ? await resizeImage(backFile) : null;

            const formData = new FormData();
            formData.append('imageFront', processedFront);
            if (processedBack) formData.append('imageBack', processedBack);
            formData.append('isBatch', 'true');

            await axios.post(`${API_URL}/scans`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            onNotification("Batch uploaded! Processing starting...", "success");
            setLastUploadStatus('success');
            
            // Reset for next batch
            setFrontFile(null);
            setBackFile(null);
            if (frontInputRef.current) frontInputRef.current.value = "";
            if (backInputRef.current) backInputRef.current.value = "";
            
        } catch (error) {
            console.error("Upload failed", error);
            onNotification("Upload failed: " + (error.response?.data?.error || error.message), "error");
            setLastUploadStatus('error');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-8">
                <h2 className={`text-xl font-bold uppercase tracking-widest mb-2 ${themeClasses.textMain}`}>⚡ Fast Batch Upload</h2>
                <p className={`${themeClasses.textMuted}`}>Upload Front & Back photos containing multiple coins. The AI will detect, crop, and identify them automatically.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div 
                    className={`${themeClasses.card} border-2 border-dashed ${isDarkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-300 hover:bg-slate-50'} rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all aspect-video relative overflow-hidden group`}
                    onDrop={(e) => handleDrop(e, 'front')}
                    onDragOver={handleDragOver}
                    onClick={() => frontInputRef.current.click()}
                >
                    <input 
                        type="file" 
                        ref={frontInputRef} 
                        onChange={(e) => handleFileChange(e, 'front')} 
                        hidden 
                        accept="image/*"
                    />
                    {frontFile && frontPreview ? (
                        <div className="w-full h-full absolute inset-0">
                            <img src={frontPreview} alt="Front" className="w-full h-full object-contain" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-white text-xs truncate">
                                Front: {frontFile.name}
                            </div>
                        </div>
                    ) : (
                        <div className={`flex flex-col items-center ${themeClasses.textMuted} group-hover:${themeClasses.accent}`}>
                            <Camera size={48} className="mb-4 opacity-50" />
                            <span className="text-sm font-bold uppercase tracking-wider">Click or Drag <strong className={themeClasses.textMain}>FRONT</strong> Photo</span>
                        </div>
                    )}
                </div>

                <div 
                    className={`${themeClasses.card} border-2 border-dashed ${isDarkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-300 hover:bg-slate-50'} rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all aspect-video relative overflow-hidden group`}
                    onDrop={(e) => handleDrop(e, 'back')}
                    onDragOver={handleDragOver}
                    onClick={() => backInputRef.current.click()}
                >
                    <input 
                        type="file" 
                        ref={backInputRef} 
                        onChange={(e) => handleFileChange(e, 'back')} 
                        hidden 
                        accept="image/*"
                    />
                    {backFile && backPreview ? (
                        <div className="w-full h-full absolute inset-0">
                            <img src={backPreview} alt="Back" className="w-full h-full object-contain" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-white text-xs truncate">
                                Back: {backFile.name}
                            </div>
                        </div>
                    ) : (
                        <div className={`flex flex-col items-center ${themeClasses.textMuted} group-hover:${themeClasses.accent}`}>
                            <Repeat size={48} className="mb-4 opacity-50" />
                            <span className="text-sm font-bold uppercase tracking-wider">Click or Drag <strong className={themeClasses.textMain}>BACK</strong> Photo</span>
                            <span className="text-xs mt-1 opacity-70">(Optional)</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end pt-6 border-t border-slate-200 dark:border-slate-800">
                <button 
                    className={`${themeClasses.accentBg} text-white px-8 py-3 rounded-lg font-bold shadow-lg shadow-teal-500/20 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                    disabled={!frontFile || isUploading}
                    onClick={handleSubmit}
                >
                    {isUploading ? (
                        <>Processing...</>
                    ) : (
                        <><Upload size={18} /> Process Batch</>
                    )}
                </button>
            </div>

            {lastUploadStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-500/10 border border-green-500/20 text-green-500 rounded-lg flex items-center gap-3">
                    <CheckCircle size={20} />
                    <span>Previous batch queued! Ready for next one.</span>
                </div>
            )}
        </div>
    );
};

export default BatchUpload;
